// ============================================
// src/App/schoolConsentGate.js
// MA-60 lane (b) — teacher/school-authority consent gate (pure core).
//
// Mirrors consentGate.js's shape: dependency-free (no supabase, no React) so
// it unit-tests in a plain .mjs and is imported by teacher.tsx (.tsx). Reuses
// the SAME `consent_records` table lane (a) uses — `consent_type` is a bare
// `text` column with no CHECK/enum, so a new value needs zero migration — and
// the SAME RLS (`parent_id = auth.uid()`): a teacher account IS a `parents`
// row (ADR-073, `classes.teacher_id → parents.id`), so a teacher's own
// authenticated session already satisfies the existing INSERT/SELECT
// policies with no schema change.
//
// Consent is scoped PER CLASS (encoded as `school_authority_v1:<classId>` in
// consent_type), not globally per teacher for life — SPEC-classroom-mode.md
// frames the acknowledgement as happening "at class creation," and a single
// global click-through would silently authorize every future class a teacher
// ever creates, at any school, forever. Per-class scoping means a teacher
// re-attests once per class (the actual chokepoint this gates,
// `addRosterNames`, is per-class already).
// ============================================

export const SCHOOL_CONSENT_PREFIX = "school_authority_v1";

/** `school_authority_v1:<classId>` — the value written to/matched against consent_type. */
export function schoolConsentType(classId) {
  return `${SCHOOL_CONSENT_PREFIX}:${classId}`;
}

/**
 * True iff `rows` contains a GRANTED, un-revoked school-authority consent row
 * scoped to EXACTLY this `classId`. A consent granted for a different class
 * (or a non-school consent, e.g. a COPPA `coppa_v1_*` row) never satisfies
 * this gate — lanes and classes don't cross-satisfy each other.
 */
export function hasSchoolAuthorityConsent(rows, classId) {
  if (!Array.isArray(rows)) return false;
  const type = schoolConsentType(classId);
  return rows.some(
    (r) =>
      r &&
      r.granted === true &&
      (r.revoked_at === null || r.revoked_at === undefined) &&
      r.consent_type === type,
  );
}

/**
 * The gate decision. Dev bypass short-circuits (e2e/dev has no real session) —
 * SAME policy as lane (a)'s `guardCloudChildWrite`. A read error is treated
 * as NO consent (fail-CLOSED): a flaky fetch must never open the gate.
 */
export async function guardSchoolRosterWrite({ classId, teacherId, fetchConsent, devBypass = false }) {
  if (devBypass) return { allow: true, reason: "dev-bypass" };
  let rows = [];
  try {
    rows = await fetchConsent(teacherId);
  } catch {
    rows = [];
  }
  return hasSchoolAuthorityConsent(rows, classId)
    ? { allow: true, reason: "consented" }
    : { allow: false, reason: "no-consent" };
}

/**
 * The real gated-write composition. `addRosterNames` (the actual point a
 * child's name enters `class_enrollments`) is called ONLY through this, so a
 * bite test that drives this function directly proves the real short-circuit
 * — not a fixture. Returns whatever `insert` returns, or null when blocked.
 */
export async function runGatedRosterWrite({ classId, teacherId, devBypass, fetchConsent, insert, onBlocked }) {
  const gate = await guardSchoolRosterWrite({ classId, teacherId, fetchConsent, devBypass });
  if (!gate.allow) {
    if (typeof onBlocked === "function") onBlocked(gate);
    return null;
  }
  return insert();
}
